import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { env } from '@shared/config/env';
import { decrypt } from '@shared/lib/security';
import { decodeJwtPayload, getOrgIdFromAccessToken, getUserTypeFromAccessToken } from '@shared/lib/jwt';
import { store } from '@shared/store';
import { logout, setAccessToken, setOrgId, setUserType } from '@shared/store/exchangeSlice';
import { refreshTokenCall } from './services';

export const serverAddress = env.apiBaseUrl;
export const devServerAddress = env.devApiBaseUrl;

const isProductionEnvironment = env.appEnvironment === 'production';
export const getApiBaseUrl = () => (isProductionEnvironment ? serverAddress : devServerAddress);

const getApiClient = () => (isProductionEnvironment ? instance : devInstance);



const getStoredAccessToken = (): string | null => {
  const stateToken = store.getState().exchange.accessToken;
  if (stateToken && stateToken !== 'null' && stateToken !== 'undefined') {
    return stateToken;
  }

  return null;
};

const instance = axios.create({
  baseURL: serverAddress,
  headers: {
    'Content-Type': 'application/json'
  }
});
const devInstance = axios.create({
  baseURL: devServerAddress,
  headers: {
    'Content-Type': 'application/json'
  }
});

axiosRetry(instance, { retries: 0 });
axiosRetry(devInstance, { retries: 0 });

let isRefreshing = false;
const refreshSubscribers: Array<(token: string | null) => void> = [];

const AUTH_LOGIN_URI = '/auth/login';
const AUTH_REFRESH_URI = '/auth/refreshToken';
const LOGIN_ROUTE = '/auth/login';
const ACCESS_TOKEN_EXPIRY_SKEW_MS = 30 * 1000;
const AUTH_BROADCAST_CHANNEL = 'auth-session-events';

const authBroadcastChannel =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel(AUTH_BROADCAST_CHANNEL)
    : null;

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

type RefreshBroadcastMessage =
  | { type: 'TOKEN_REFRESHED'; token: string }
  | { type: 'SIGNED_OUT' };

const getCookieValue = (key: string): string | null => {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${key}=`));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split('=').slice(1).join('='));
};

const clearAuthSession = () => {
  store.dispatch(logout());
  sessionStorage.clear();
  document.cookie = 'refreshToken=; Max-Age=0; path=/; SameSite=Lax';
};

const setAuthorizationHeader = (config: AxiosRequestConfig, token: string) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }

  if (config.headers instanceof AxiosHeaders) {
    config.headers.set('Authorization', `Bearer ${token}`);
    return;
  }

  (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
};

const notifyRefreshSubscribers = (token: string | null) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers.length = 0;
};

const waitForRefresh = () =>
  new Promise<string>((resolve, reject) => {
    refreshSubscribers.push((token) => {
      if (token) {
        resolve(token);
      } else {
        reject(new Error('Failed to refresh access token.'));
      }
    });
  });

const isAuthEndpoint = (url?: string) =>
  Boolean(url?.includes(AUTH_LOGIN_URI) || url?.includes(AUTH_REFRESH_URI));

export const getAccessTokenExpiryMs = (token: string): number | null => {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== 'number') {
    return null;
  }

  return exp * 1000;
};

export const isAccessTokenExpiring = (
  token: string,
  skewMs: number = ACCESS_TOKEN_EXPIRY_SKEW_MS
): boolean => {
  const tokenExpiryMs = getAccessTokenExpiryMs(token);
  if (!tokenExpiryMs) {
    return false;
  }

  return tokenExpiryMs - Date.now() <= skewMs;
};

const broadcastAuthMessage = (message: RefreshBroadcastMessage) => {
  authBroadcastChannel?.postMessage(message);
};

export const broadcastAccessToken = (token: string) => {
  broadcastAuthMessage({ type: 'TOKEN_REFRESHED', token });
};

export const broadcastSignedOut = () => {
  broadcastAuthMessage({ type: 'SIGNED_OUT' });
};

const executeRefreshTokenFlow = async (): Promise<string> => {
  const refreshToken = getCookieValue('refreshToken');
  // const refreshResponse = await axios.post<{
  //   code?: number;
  //   data?: { accessToken?: string;};
  // }>(
  //   `${serverAddress}${authRefreshTokenURI}`,
  //   refreshToken ? { refreshToken } : {},
  //   { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
  // );

  const refreshData = await refreshTokenCall(refreshToken);
  const newAccessToken = (refreshData?.data || {}).accessToken ;

  if (refreshData?.code !== 200 || typeof newAccessToken !== 'string') {
    throw new Error('Refresh token API did not return a valid access token.');
  }

  store.dispatch(setAccessToken(newAccessToken));
  store.dispatch(setUserType(getUserTypeFromAccessToken(newAccessToken)));
  store.dispatch(setOrgId(getOrgIdFromAccessToken(newAccessToken)));
  return newAccessToken;
};

export const refreshAccessToken = async (redirectOnFailure = true): Promise<string> => {
  if (isRefreshing) {
    return waitForRefresh();
  }

  isRefreshing = true;
  try {
    const newAccessToken = await executeRefreshTokenFlow();
    notifyRefreshSubscribers(newAccessToken);
    broadcastAccessToken(newAccessToken);
    return newAccessToken;
  } catch (refreshError) {
    clearAuthSession();
    notifyRefreshSubscribers(null);
    broadcastSignedOut();
    if (redirectOnFailure && window.location.pathname !== LOGIN_ROUTE) {
      window.location.href = LOGIN_ROUTE;
    }
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

const withAuthHeader = async (
  config: InternalAxiosRequestConfig,
  redirectOnFailure = true
): Promise<InternalAxiosRequestConfig> => {
  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }

  if (isAuthEndpoint(config.url)) {
    return config;
  }

  let token = getStoredAccessToken();
  if (!token) {
    return config;
  }

  if (isAccessTokenExpiring(token)) {
    token = await refreshAccessToken(redirectOnFailure);
  }

  setAuthorizationHeader(config, token);
  return config;
};

instance.interceptors.request.use(
  (config) => withAuthHeader(config),
  (error) => Promise.reject(error)
);
devInstance.interceptors.request.use(
  (config) => withAuthHeader(config),
  (error) => Promise.reject(error)
);

const onAuthResponseError = async (error: AxiosError, client: typeof instance) => {
  const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;
  const status = error.response?.status;

  if (!originalRequest || status !== 401 || originalRequest._retry || isAuthEndpoint(originalRequest.url)) {
    return Promise.reject(error);
  }

  originalRequest._retry = true;

  try {
    const newAccessToken = await refreshAccessToken();
    setAuthorizationHeader(originalRequest, newAccessToken);
    return client(originalRequest);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
};

instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => onAuthResponseError(error, instance)
);
devInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => onAuthResponseError(error, devInstance)
);

const applyAuthSyncMessage = (message: unknown) => {
  if (!message || typeof message !== 'object') {
    return;
  }

  const authMessage = message as RefreshBroadcastMessage;
  if (authMessage.type === 'TOKEN_REFRESHED' && typeof authMessage.token === 'string') {
    store.dispatch(setAccessToken(authMessage.token));
    store.dispatch(setUserType(getUserTypeFromAccessToken(authMessage.token)));
    store.dispatch(setOrgId(getOrgIdFromAccessToken(authMessage.token)));
    return;
  }

  if (authMessage.type === 'SIGNED_OUT') {
    clearAuthSession();
  }
};

if (authBroadcastChannel) {
  authBroadcastChannel.onmessage = (event: MessageEvent<RefreshBroadcastMessage>) => {
    applyAuthSyncMessage(event.data);
  };
}

const apiRequest = async <T = unknown, R = unknown>(
  method: AxiosRequestConfig['method'],
  url: string,
  data?: T,
  config: AxiosRequestConfig = {}
): Promise<R> => {
  try {
    const client = getApiClient();
    const response: AxiosResponse<R> = await client({
      method,
      url,
      data,
      ...config,
      baseURL: config.baseURL || getApiBaseUrl()
    });

    const encryptedHeader =
      config.headers instanceof AxiosHeaders
        ? config.headers.get('X-Encrypted')
        : (config.headers as Record<string, string | boolean | undefined> | undefined)?.['X-Encrypted'];

    if (encryptedHeader === true || encryptedHeader === 'true') {
      return (await decrypt(response.data as string, url)) as R;
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data ?? error.response?.statusText ?? 'Request failed';
      throw new ApiRequestError(typeof apiError === 'string' ? apiError : JSON.stringify(apiError), {
        status: error.response?.status,
        data: error.response?.data
      });
    }

    throw new ApiRequestError('An unexpected error occurred. Please try again.');
  }
};

const api = {
  get: <R = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<undefined, R>('get', url, undefined, config),
  post: <T = unknown, R = unknown>(url: string, data: T, config?: AxiosRequestConfig) =>
    apiRequest<T, R>('post', url, data, config),
  put: <T = unknown, R = unknown>(url: string, data: T, config?: AxiosRequestConfig) =>
    apiRequest<T, R>('put', url, data, config),
  delete: <R = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<undefined, R>('delete', url, undefined, config),
  upload: <R = unknown>(url: string, formData: FormData, config?: AxiosRequestConfig) =>
    apiRequest<FormData, R>('post', url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...config
    }),
  download: async (url: string, config: AxiosRequestConfig = {}): Promise<Blob> => {
    const client = getApiClient();
    const response: AxiosResponse<Blob> = await client.get(url, {
      responseType: 'blob',
      baseURL: config.baseURL || getApiBaseUrl(),
      ...config
    });
    return response.data;
  }
};

export default api;
export { instance as internalApi, devInstance as devInternalApi };
export class ApiRequestError extends Error {
  status?: number;
  data?: unknown;

  constructor(message: string, options: { status?: number; data?: unknown } = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = options.status;
    this.data = options.data;
  }
}
